/* ═══════════════════════════════════════════════════════════════════════
   CART PHONE GATE
   Enforces required-phone capture before checkout. Runs on both the cart
   page and the cart drawer — the snippet may render twice in the DOM, so
   this script wires up every instance independently.

   Wiring:
     • Pre-fill from cart.attributes.Phone (handled in Liquid — see snippet).
     • Validate on input + blur (≥10 digits).
     • On valid input → POST /cart/update.json {attributes:{Phone: ...}}
       so the value lands on the order even if the customer never types
       it again at the Shopify checkout.
     • Disable Checkout + Shop Pay + any "express checkout" buttons by
       adding .cart-checkout-gated to a wrapping container — CSS handles
       the visual disable + pointer-events.
     • Intercept clicks on the gated buttons as belt-and-suspenders in
       case CSS is bypassed.

   Mike + Ryan 2026-05-14: Shopify native "require phone" only applies
   to the shipping address form, which pickup orders skip. This script
   closes that gap by capturing phone in the cart attribute so every
   order — pickup, delivery, shipping — has a phone Renee can call.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* Match North American 10-digit numbers (Cupcake Co's market). Allow
     optional +1 country code and any non-digit separators in between. */
  var DIGIT_RE = /\d/g;
  function digitsOnly(str) { return (str.match(DIGIT_RE) || []).join(''); }
  function isValidPhone(str) {
    var d = digitsOnly(str);
    /* 10 digits = bare US number, 11 digits = leading 1 country code. */
    return d.length === 10 || (d.length === 11 && d[0] === '1');
  }

  /* Pretty-format as the user types: (555) 123-4567 / +1 (555) 123-4567.
     Non-destructive — preserves the raw digits so backspace works. */
  function formatPhone(str) {
    var d = digitsOnly(str);
    if (!d) return '';
    if (d.length === 11 && d[0] === '1') d = d.slice(1);
    if (d.length < 4)  return '(' + d;
    if (d.length < 7)  return '(' + d.slice(0,3) + ') ' + d.slice(3);
    if (d.length <= 10) return '(' + d.slice(0,3) + ') ' + d.slice(3,6) + '-' + d.slice(6, 10);
    /* Anything longer — show first 10 digits, drop the rest. */
    return '(' + d.slice(0,3) + ') ' + d.slice(3,6) + '-' + d.slice(6, 10);
  }

  /* Save to cart attribute. Debounced so we don't hammer /cart/update.json
     on every keystroke. */
  var saveTimer = null;
  function saveToCart(value) {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      var body = new FormData();
      body.append('attributes[Phone]', value);
      fetch('/cart/update.js', { method: 'POST', body: body, credentials: 'same-origin' })
        .catch(function () { /* swallow — UX continues; cart attribute may retry on next save */ });
    }, 350);
  }

  /* Find every gateable button across the page. We re-query on each
     state update so newly-injected buttons (e.g. Shop Pay loaded
     asynchronously) get gated too. */
  function gateableButtons() {
    return Array.from(document.querySelectorAll([
      'button[name="checkout"]',                    /* native Checkout button */
      '.checkout-button',                           /* theme checkout class */
      '.cart__dynamic-checkout-buttons',            /* Shop Pay / express container */
      '.additional-checkout-buttons',
      '[data-shopify="dynamic-checkout-cart"]',     /* Shopify-injected dynamic checkout */
      'shop-pay-wallet-button',                     /* Shop Pay web component */
      'a[href*="/checkout"]'                        /* any anchor to checkout */
    ].join(',')));
  }

  /* Find every cart gate instance — typically one on the cart page,
     plus one in the drawer. Each gets its own listeners + state. */
  function setupAll() {
    var gates = document.querySelectorAll('[data-cart-phone-gate]');
    gates.forEach(setupOne);
    /* Initial sync: if no gate is on this page (e.g. /products/...),
       don't gate anything. If at least one gate exists and is invalid,
       gate. */
    if (gates.length) syncGatedState();
  }

  function setupOne(gate) {
    if (gate.dataset.cartPhoneInit === '1') return;
    gate.dataset.cartPhoneInit = '1';

    var input = gate.querySelector('[data-cart-phone-input]');
    var errorBox = gate.querySelector('[data-cart-phone-error]');
    if (!input) return;

    /* Format the pre-filled value if it came from cart attributes. */
    if (input.value) input.value = formatPhone(input.value);

    function setError(msg) {
      if (msg) {
        errorBox.textContent = msg;
        errorBox.hidden = false;
        input.classList.add('is-invalid');
        input.classList.remove('is-valid');
      } else {
        errorBox.hidden = true;
        input.classList.remove('is-invalid');
        if (input.value) input.classList.add('is-valid');
      }
    }

    function onInput() {
      /* Preserve caret position around the formatting churn — store cursor,
         format, then restore an approximate equivalent caret index. The
         exact restoration is imperfect for inserts mid-string, but works
         well for the common case of appending at the end. */
      var caretAtEnd = input.selectionStart === input.value.length;
      var formatted = formatPhone(input.value);
      if (formatted !== input.value) {
        input.value = formatted;
        if (caretAtEnd) {
          input.setSelectionRange(formatted.length, formatted.length);
        }
      }
      if (isValidPhone(input.value)) {
        setError(null);
        saveToCart(input.value);
      } else if (input.value.length === 0) {
        /* Empty — clear error and let blur handler complain later. */
        setError(null);
      } else {
        /* Mid-typing, partial value — don't show an error until blur. */
        input.classList.remove('is-valid');
        input.classList.remove('is-invalid');
      }
      syncGatedState();
    }

    function onBlur() {
      if (!input.value.trim()) {
        setError('Phone number is required to check out.');
      } else if (!isValidPhone(input.value)) {
        setError('Please enter a 10-digit phone number — like (555) 123-4567.');
      } else {
        setError(null);
        /* Force-save on blur in case the debounce hasn't fired yet. */
        saveToCart(input.value);
      }
      syncGatedState();
    }

    input.addEventListener('input', onInput);
    input.addEventListener('blur', onBlur);
  }

  function isAnyGateValid() {
    var inputs = document.querySelectorAll('[data-cart-phone-input]');
    if (inputs.length === 0) return true; /* no gate rendered (e.g. on PDP) */
    /* All visible gates must have valid phones. If any single gate input
       is valid, treat the order as gated-clear — both surfaces share the
       same cart attribute, so filling one is enough. */
    for (var i = 0; i < inputs.length; i++) {
      if (isValidPhone(inputs[i].value)) return true;
    }
    return false;
  }

  function syncGatedState() {
    var ok = isAnyGateValid();
    /* Apply/remove the gating class on the closest meaningful container.
       Using documentElement makes the CSS rules in cart-phone-gate.css
       apply to every checkout button on the page regardless of which
       cart surface (page vs drawer) holds them. */
    document.documentElement.classList.toggle('cart-checkout-gated', !ok);
  }

  /* Belt-and-suspenders: even if CSS pointer-events is bypassed, intercept
     clicks on any gateable checkout button when phone isn't valid yet. */
  document.addEventListener('click', function (e) {
    if (isAnyGateValid()) return;
    var btn = e.target.closest && e.target.closest([
      'button[name="checkout"]',
      '.checkout-button',
      '.cart__dynamic-checkout-buttons',
      '.additional-checkout-buttons',
      'a[href*="/checkout"]'
    ].join(','));
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    /* Focus the first phone input + scroll into view so the user knows
       what they need to do. */
    var first = document.querySelector('[data-cart-phone-input]');
    if (first) {
      first.focus();
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var gate = first.closest('[data-cart-phone-gate]');
      var errorBox = gate && gate.querySelector('[data-cart-phone-error]');
      if (errorBox) {
        errorBox.textContent = 'Please add a phone number before checking out.';
        errorBox.hidden = false;
      }
    }
  }, true);

  /* Boot — twice: once on DOMContentLoaded for the cart page, and once
     more after the cart drawer is dynamically opened (Shopify's drawer
     re-injects on open). MutationObserver catches anything added after. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAll);
  } else {
    setupAll();
  }
  var mo = new MutationObserver(function () { setupAll(); });
  mo.observe(document.body, { childList: true, subtree: true });

  /* Also re-sync when Shopify's cart drawer dispatches its open events. */
  document.addEventListener('cart:open', setupAll);
  document.addEventListener('cart:refresh', setupAll);
})();
